import React, { useState, useEffect, Fragment } from 'react';
import ComplaintService from '../services/complaint.service';
import '../styles/ComplaintList.css';
import { Formik, Field, ErrorMessage, Form } from 'formik';
import * as Yup from 'yup';

interface Complaint {
  id: string;
  subject: string;
  description: string;
  status: 'Pending' | 'Ongoing' | 'Completed';
  created_at: string;
  category_id: number;
  photo_url?: string;
}

interface Category {
  id: number;
  title: string;
}

interface ComplaintListProps {
  onViewDetail?: (id: string) => void;
  refreshTrigger?: boolean; // Will change when parent wants to refresh the list
  onComplaintUpdated?: () => void; // Callback to notify parent about updates
}

const ComplaintList: React.FC<ComplaintListProps> = ({ onViewDetail, refreshTrigger, onComplaintUpdated }) => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [categories, setCategories] = useState<Record<number, string>>({});  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // For edit functionality
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [complaintToEdit, setComplaintToEdit] = useState<Complaint | null>(null);
  
  // For delete functionality 
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [complaintIdToDelete, setComplaintIdToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);  useEffect(() => {
    // Load complaints when component mounts or when page/search/filter changes
    const fetchComplaints = async () => {
      setLoading(true);
      try {
        const response = await ComplaintService.getMyComplaints(search, page, 10);
        
        console.log('API response:', response);
        
        // Apply status filter if one is selected
        const filteredData = statusFilter === 'all' 
          ? response.data 
          : response.data.filter((complaint: Complaint) => complaint.status === statusFilter);
        
        console.log('Filtered complaints:', filteredData);
        
        setComplaints(filteredData);
        setTotalPages(response.totalPages);
      } catch (err: any) {
        console.error('Error fetching complaints:', err);
        setError('Failed to load complaints. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Load categories
    const fetchCategories = async () => {
      try {
        const response = await ComplaintService.getCategories();
        const categoryMap: Record<number, string> = {};
        response.data.forEach((cat: Category) => {
          categoryMap[cat.id] = cat.title;
        });
        setCategories(categoryMap);
      } catch (err) {
        console.error('Failed to load categories', err);
      }
    };    fetchCategories();
    fetchComplaints();
  }, [page, search, statusFilter, refreshTrigger]);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };  // Format UUID to show only first 8 characters
  const formatId = (id: string) => {
    if (typeof id === 'string') {
      return id.substring(0, 8) + '...';
    }
    return 'N/A';
  };

  // Handle edit button click
  const handleEdit = (complaint: Complaint) => {
    setComplaintToEdit(complaint);
    setEditModalVisible(true);
  };

  // Handle delete button click
  const handleDelete = (id: string) => {
    setComplaintIdToDelete(id);
    setDeleteModalVisible(true);
  };
  // Confirm deletion
  const confirmDelete = async () => {
    if (!complaintIdToDelete) return;
    
    setDeleteLoading(true);
    try {
      await ComplaintService.deleteComplaint(complaintIdToDelete);
      // Filter out the deleted complaint from the state
      setComplaints(complaints.filter(c => c.id !== complaintIdToDelete));
      setSuccessMessage('Complaint deleted successfully');
      // Hide message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      setDeleteModalVisible(false);
      // Notify parent component
      if (onComplaintUpdated) {
        onComplaintUpdated();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'An error occurred while deleting the complaint');
    } finally {
      setDeleteLoading(false);
    }
  };
  // Handle edit form submission
  const handleEditSubmit = async (values: any) => {
    if (!complaintToEdit) return;
    
    try {
      await ComplaintService.updateComplaint(complaintToEdit.id, {
        category_id: values.category_id,
        subject: values.subject,
        description: values.description
      });
      
      // Update the complaint in the state
      setComplaints(complaints.map(c => 
        c.id === complaintToEdit.id 
          ? { ...c, subject: values.subject, description: values.description, category_id: values.category_id } 
          : c
      ));
      
      setSuccessMessage('Complaint updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditModalVisible(false);
      // Notify parent component
      if (onComplaintUpdated) {
        onComplaintUpdated();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'An error occurred while updating the complaint');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    // The useEffect will automatically trigger and fetch complaints with the updated search term
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800';
      case 'Ongoing':
        return 'inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800';
      case 'Completed':
        return 'inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800';
      default:
        return 'inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="complaint-list-container">
      <h2>My Maintenance Requests</h2>
        <div className="mb-6">
        <form onSubmit={handleSearch} className="flex">
          <input
            type="text"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Search by subject, description or status"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded-r hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Search
          </button>
        </form>
      </div>
      
      <div className="flex justify-between items-center mb-6">
        <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Status filter">
          <button 
            type="button" 
            className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
              statusFilter === 'all' 
              ? 'bg-blue-600 text-white' 
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
            }`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button 
            type="button" 
            className={`px-4 py-2 text-sm font-medium border-t border-b ${
              statusFilter === 'Pending' 
              ? 'bg-yellow-500 text-white border-yellow-500' 
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
            onClick={() => setStatusFilter('Pending')}
          >
            Pending
          </button>
          <button 
            type="button" 
            className={`px-4 py-2 text-sm font-medium border-t border-b ${
              statusFilter === 'Ongoing' 
              ? 'bg-blue-500 text-white border-blue-500' 
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
            onClick={() => setStatusFilter('Ongoing')}
          >
            Ongoing
          </button>
          <button 
            type="button" 
            className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
              statusFilter === 'Completed' 
              ? 'bg-green-600 text-white border-green-600' 
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
            }`}
            onClick={() => setStatusFilter('Completed')}
          >
            Completed
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
        {loading ? (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : complaints.length === 0 ? (
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-6">
          <p>You have not submitted any maintenance requests yet.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted On</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {complaints.map((complaint) => (
                  <tr key={complaint.id ? complaint.id : `complaint-${Math.random()}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" title={complaint.id}>
                      {complaint.id ? formatId(complaint.id) : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {complaint.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {categories[complaint.category_id] || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadgeClass(complaint.status)}>
                        {complaint.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(complaint.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-xs font-medium"
                          onClick={() => onViewDetail && onViewDetail(complaint.id)}
                          aria-label="View details"
                        >
                          View
                        </button>
                        {complaint.status === 'Pending' && (
                          <>
                            <button 
                              className="bg-yellow-500 hover:bg-yellow-600 text-white py-1 px-3 rounded text-xs font-medium"
                              onClick={() => handleEdit(complaint)}
                              aria-label="Edit complaint"
                            >
                              Edit
                            </button>
                            <button 
                              className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded text-xs font-medium"
                              onClick={() => handleDelete(complaint.id)}
                              aria-label="Delete complaint"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <ul className="pagination">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </button>
                </li>
                  {/* Show up to 5 page numbers */}
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  // Center current page in pagination
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${page === pageNum ? 'active' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}        </>
      )}      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mt-3 relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}{/* Edit Modal */}
      {editModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Maintenance Request</h3>
              <button 
                onClick={() => setEditModalVisible(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {complaintToEdit && (
              <Formik
                initialValues={{
                  category_id: complaintToEdit.category_id,
                  subject: complaintToEdit.subject,
                  description: complaintToEdit.description
                }}
                validationSchema={Yup.object({
                  category_id: Yup.number().required('Category is required'),
                  subject: Yup.string()
                    .min(5, 'Title is too short')
                    .max(100, 'Title is too long')
                    .required('Title is required'),
                  description: Yup.string()
                    .min(10, 'Description is too short')
                    .max(1000, 'Description is too long')
                    .required('Description is required')
                })}
                onSubmit={handleEditSubmit}
              >
                {formik => (
                  <Form className="space-y-4">
                    <div>
                      <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <Field 
                        as="select" 
                        name="category_id"
                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md ${formik.touched.category_id && formik.errors.category_id ? 'border-red-500' : ''}`}
                      >
                        {Object.entries(categories).map(([id, title]) => (
                          <option key={id} value={Number(id)}>{title}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="category_id" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <Field
                        type="text"
                        name="subject"
                        className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${formik.touched.subject && formik.errors.subject ? 'border-red-500' : ''}`}
                      />
                      <ErrorMessage name="subject" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <Field
                        as="textarea"
                        rows={5}
                        name="description"
                        className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${formik.touched.description && formik.errors.description ? 'border-red-500' : ''}`}
                      />
                      <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div className="flex justify-end space-x-3 mt-5">
                      <button 
                        type="button"
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        onClick={() => setEditModalVisible(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        disabled={formik.isSubmitting}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      )}      {/* Delete Confirmation Modal */}
      {deleteModalVisible && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Delete Maintenance Request</h3>
              <button 
                onClick={() => setDeleteModalVisible(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mt-3">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this maintenance request? This action cannot be undone.
              </p>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setDeleteModalVisible(false)}
              >
                Cancel
              </button>
              <button 
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintList;
